import React from 'react';
import { Control, Path, FieldValues, useWatch } from 'react-hook-form';

// import { Spinner } from '../../index';

import { FormSelect } from '../form-fields/form-select';

interface DependentSelectProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  dependsOn: Path<T>;
  getOptions: (
    dependentValue: unknown
  ) => Promise<{ value: string; label: string }[]> | { value: string; label: string }[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  cacheDuration?: number;
  onOptionsLoad?: (options: { value: string; label: string }[]) => void;
  onLoadError?: (error: Error) => void;
}

export function DependentSelect<T extends FieldValues>({
  control,
  name,
  label,
  dependsOn,
  getOptions,
  placeholder = 'Seçiniz',
  error,
  disabled = false,
  cacheDuration = 5 * 60 * 1000, // 5 minutes default
  onOptionsLoad,
  onLoadError,
}: DependentSelectProps<T>) {
  const dependentValue = useWatch({
    control,
    name: dependsOn,
  });

  const [options, setOptions] = React.useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  // Cache for loaded options
  const optionsCacheRef = React.useRef<
    Map<
      string,
      {
        options: { value: string; label: string }[];
        timestamp: number;
      }
    >
  >(new Map());

  React.useEffect(() => {
    if (!dependentValue) {
      setOptions([]);
      return;
    }

    const cacheKey = JSON.stringify(dependentValue);
    const cached = optionsCacheRef.current.get(cacheKey);

    // Check if cached data is still valid
    if (cached && Date.now() - cached.timestamp < cacheDuration) {
      setOptions(cached.options);
      return;
    }

    // Load options
    const loadOptions = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const result = await getOptions(dependentValue);
        const optionsList = Array.isArray(result) ? result : await result;

        setOptions(optionsList);

        // Cache the result
        optionsCacheRef.current.set(cacheKey, {
          options: optionsList,
          timestamp: Date.now(),
        });

        onOptionsLoad?.(optionsList);
      } catch (err) {
        const error = err as Error;
        setLoadError(error.message || 'Seçenekler yüklenirken hata oluştu');
        setOptions([]);
        onLoadError?.(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOptions();
  }, [dependentValue, getOptions, cacheDuration, onOptionsLoad, onLoadError]);

  // Clear cache on unmount
  React.useEffect(() => {
    const cache = optionsCacheRef.current;
    return () => {
      cache.clear();
    };
  }, []);

  const allOptions = [{ value: '', label: placeholder }, ...options];

  return (
    <FormSelect
      control={control}
      name={name}
      label={label}
      options={allOptions}
      error={error || loadError || undefined}
      disabled={disabled || isLoading || !dependentValue}
    />
  );
}

// Helper component for chained dependent selects
interface ChainedDependentSelectsProps<T extends FieldValues> {
  control: Control<T>;
  chain: Array<{
    name: Path<T>;
    label: string;
    dependsOn?: Path<T>;
    getOptions: (
      dependentValue: unknown
    ) => Promise<{ value: string; label: string }[]> | { value: string; label: string }[];
    placeholder?: string;
  }>;
  errors?: Record<string, string>;
}

export function ChainedDependentSelects<T extends FieldValues>({
  control,
  chain,
  errors = {},
}: ChainedDependentSelectsProps<T>) {
  return (
    <>
      {chain.map((config, index) => {
        if (index === 0 && !config.dependsOn) {
          // First select in chain (independent)
          return (
            <FormSelect
              key={config.name}
              control={control}
              name={config.name}
              label={config.label}
              options={[
                { value: '', label: config.placeholder || 'Seçiniz' },
                ...(typeof config.getOptions === 'function'
                  ? (config.getOptions(null) as { value: string; label: string }[])
                  : []),
              ]}
              error={errors[config.name]}
            />
          );
        }

        return (
          <DependentSelect
            key={config.name}
            control={control}
            name={config.name}
            label={config.label}
            dependsOn={config.dependsOn || chain[index - 1].name}
            getOptions={config.getOptions}
            placeholder={config.placeholder}
            error={errors[config.name]}
          />
        );
      })}
    </>
  );
}

// Example data source for dependent selects
export const dependentDataSources = {
  // Turkey provinces and districts
  turkeyLocations: {
    getProvinces: () => [
      { value: '34', label: 'İstanbul' },
      { value: '06', label: 'Ankara' },
      { value: '35', label: 'İzmir' },
      { value: '01', label: 'Adana' },
      { value: '07', label: 'Antalya' },
    ],

    getDistricts: async (provinceCode: string) => {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      const districtMap: Record<string, { value: string; label: string }[]> = {
        '34': [
          { value: 'besiktas', label: 'Beşiktaş' },
          { value: 'kadikoy', label: 'Kadıköy' },
          { value: 'uskudar', label: 'Üsküdar' },
          { value: 'sisli', label: 'Şişli' },
        ],
        '06': [
          { value: 'cankaya', label: 'Çankaya' },
          { value: 'kecioren', label: 'Keçiören' },
          { value: 'mamak', label: 'Mamak' },
          { value: 'yenimahalle', label: 'Yenimahalle' },
        ],
        '35': [
          { value: 'konak', label: 'Konak' },
          { value: 'bornova', label: 'Bornova' },
          { value: 'karsiyaka', label: 'Karşıyaka' },
          { value: 'buca', label: 'Buca' },
        ],
      };

      return districtMap[provinceCode] || [];
    },
  },

  // Vehicle brands and models
  vehicleData: {
    getBrands: () => [
      { value: 'mercedes', label: 'Mercedes-Benz' },
      { value: 'man', label: 'MAN' },
      { value: 'scania', label: 'Scania' },
      { value: 'volvo', label: 'Volvo' },
      { value: 'ford', label: 'Ford' },
    ],

    getModels: async (brand: string) => {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 300));

      const modelMap: Record<string, { value: string; label: string }[]> = {
        mercedes: [
          { value: 'actros', label: 'Actros' },
          { value: 'atego', label: 'Atego' },
          { value: 'sprinter', label: 'Sprinter' },
        ],
        man: [
          { value: 'tgx', label: 'TGX' },
          { value: 'tgs', label: 'TGS' },
          { value: 'tgm', label: 'TGM' },
        ],
        scania: [
          { value: 'r-series', label: 'R Series' },
          { value: 's-series', label: 'S Series' },
          { value: 'p-series', label: 'P Series' },
        ],
      };

      return modelMap[brand] || [];
    },
  },
};
